import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Grid,
  Flex,
  Progress,
  VStack,
  Icon,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FaRoute, FaCodeBranch } from 'react-icons/fa';
import moment from 'moment';
import API from '../../../helpers/axiosInstance';

const FallbackPerformanceAnalysis = ({ date = new Date(), refresh }) => {
  const [loading, setLoading] = useState(false);
  const [fallbackData, setFallbackData] = useState(null);
  const [noDataMode, setNoDataMode] = useState(false);

  const getApiHeaders = () => ({
    "x-api-key": localStorage.getItem("api-key"),
    "x-access-token": localStorage.getItem("customer-portal"),
  });

  useEffect(() => {
    fetchFallbackData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const fetchFallbackData = async () => {
    setLoading(true);
    try {
      const dateFormatted = moment(date);
      const month = dateFormatted.format("MM");
      const year = dateFormatted.format("YYYY");

      const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/fallback-performance?month=${month}&year=${year}${refresh}`;
      const response = await API.get(url, { headers: getApiHeaders() });

      if (response.data.success && response.data.data.overview) {
        setFallbackData(response.data.data);
        setNoDataMode(response.data.data.overview.totalMessages === 0);
      }
    } catch (error) {
      console.error("Error fetching fallback analytics:", error);
      setNoDataMode(true);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    if (!fallbackData || noDataMode) return null;

    const { overview, mostCommonFallbackPaths, fallbackStepFlow } = fallbackData;

    return {
      totalMessages: overview.totalMessages,
      successfullyDelivered: overview.successfullyDelivered,
      finalFailures: overview.finalFailures,
      successRate: overview.successRate,
      commonPaths: mostCommonFallbackPaths.map(path => ({
        path: path.path,
        count: path.messages,
        percentage: path.successRate,
        successRate: path.successRate
      })),
      fallbackSteps: fallbackStepFlow.filter(step => step.received > 0)
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="#155dfc" />
      </Center>
    );
  }

  return (
    <Card
      bg={{ base: 'white', _dark: 'gray.800' }}
      borderWidth="1px"
      borderColor={{ base: '#0000001a', _dark: 'gray.600' }}
      shadow="sm"
    >
      <CardHeader>
        <Flex align="center" gap={2} mb={2}>
          <Icon as={FaRoute} w={5} h={5} color="#155dfc" />
          <Heading size="md">Detailed Fallback Performance Analysis</Heading>
        </Flex>
        <Text fontSize="sm" color="gray.600">
          Comprehensive insights into your automatic channel routing system performance
        </Text>
      </CardHeader>

      <CardBody>
        <VStack spacing={6} align="stretch">
          {noDataMode ? (
            <>
              {/* Empty Fallback Paths */}
              <Box>
                <Flex align="center" gap={2} mb={4}>
                  <Icon as={FaCodeBranch} w={5} h={5} color="gray.400" />
                  <Heading size="md" color="gray.600">Most Common Fallback Paths</Heading>
                </Flex>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  {[1, 2, 3, 4].map((index) => (
                    <Box
                      key={index}
                      p={4}
                      bg={{ base: '#fbf9faae', _dark: 'gray.700' }}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={{ base: '#0000001a', _dark: 'gray.600' }}
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontSize="sm" fontWeight="medium" color="gray.400">
                          No fallback data available
                        </Text>
                        <Text fontSize="lg" fontWeight="bold" color="gray.400">
                          --%
                        </Text>
                      </Flex>
                      <Flex align="center" gap={2}>
                        <Text fontSize="sm" color="gray.400">-- messages</Text>
                        <Box flex={1}>
                          <Progress
                            value={0}
                            size="sm"
                            sx={{ "& > div": { background: "var(--brand-primary)" } }}
                            bg="gray.200"
                            borderRadius="full"
                          />
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </Grid>
              </Box>

              {/* Empty Message Journey */}
              <Box>
                <Flex align="center" gap={2} mb={4}>
                  <Icon as={FaRoute} w={5} h={5} color="gray.400" />
                  <Heading size="md" color="gray.600">Message Journey Flow</Heading>
                </Flex>
                <Center
                  bg={{ base: '#fbf9faae', _dark: 'gray.700' }}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={{ base: '#0000001a', _dark: 'gray.600' }}
                  flexDirection="column"
                >
                  <Icon as={FaRoute} w={12} h={12} color="gray.300" mb={2} />
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    No fallback journeys to display
                  </Text>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Start sending messages to see fallback analytics
                  </Text>
                </Center>
              </Box>
            </>
          ) : stats ? (
            <>
              {/* Common Fallback Paths */}
              <Box>
                <Flex align="center" gap={2} mb={4}>
                  <Icon as={FaCodeBranch} w={5} h={5} color="#155dfc" />
                  <Heading size="md">Most Common Fallback Paths</Heading>
                </Flex>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  {stats.commonPaths.map((path, index) => (
                    <Box
                      key={index}
                      p={4}
                      bg={{ base: '#fbf9faae', _dark: 'gray.700' }}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={{ base: '#0000001a', _dark: 'gray.600' }}
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                          {path.path}
                        </Text>
                        <Text fontSize="lg" fontWeight="bold" color="#155dfc">
                          {path.percentage}%
                        </Text>
                      </Flex>
                      <Flex align="center" gap={2}>
                        <Text fontSize="sm" color="gray.600">
                          {path.count.toLocaleString()} messages
                        </Text>
                        <Box flex={1}>
                          <Progress
                            value={Math.min(parseFloat(path.percentage), 100)}
                            size="sm"
                            sx={{ "& > div": { background: "var(--brand-primary)" } }}
                            bg="gray.200"
                            borderRadius="full"
                          />
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </Grid>
              </Box>

              {/* Message Journey Flow Explanation */}
              <Box>
                <Flex align="center" gap={2} mb={4}>
                  <Icon as={FaRoute} w={5} h={5} color="#155dfc" />
                  <Heading size="md">Message Journey Flow Explanation</Heading>
                </Flex>

                {/* Introduction */}
                <Box
                  bgGradient="linear(to-r, var(--brand-50), var(--brand-50))"
                  p={4}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="blue.200"
                  mb={6}
                >
                  <Text fontSize="sm" color="gray.700" mb={3}>
                    <strong>How Fallback Works:</strong> When a message fails to deliver through one channel,
                    our system automatically tries the next channel in the priority order until successful delivery or all channels are exhausted.
                  </Text>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} fontSize="sm">
                    <Box bg="#ffffffb3" p={3} borderRadius="lg">
                      <Text color="#155dfc" fontWeight="medium" mb={1}>
                        Total Messages Sent
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                        {stats.totalMessages.toLocaleString()}
                      </Text>
                    </Box>
                    <Box bg="#ffffffb3" p={3} borderRadius="lg">
                      <Text color="green.600" fontWeight="medium" mb={1}>
                        Successfully Delivered
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="#008236">
                        {stats.successfullyDelivered.toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color="green.600">
                        {stats.successRate.toFixed(1)}% success rate
                      </Text>
                    </Box>
                    <Box bg="#ffffffb3" p={3} borderRadius="lg">
                      <Text color="red.600" fontWeight="medium" mb={1}>
                        Final Failures
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="red.700">
                        {stats.finalFailures.toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color="red.600">
                        {((stats.finalFailures / stats.totalMessages) * 100).toFixed(1)}% failure rate
                      </Text>
                    </Box>
                  </Grid>
                </Box>

                {/* Step-by-step Journey */}
                <VStack spacing={4} align="stretch">
                  {stats.fallbackSteps.map((step, index) => {
                    const isLastStep = index === stats.fallbackSteps.length - 1;
                    const successRate = step.successRate;

                    return (
                      <Box key={index} position="relative">
                        {/* Connection Line */}
                        {index > 0 && (
                          <Box
                            position="absolute"
                            left="32px"
                            top="-16px"
                            w="2px"
                            h="16px"
                            bg="gray.300"
                          />
                        )}

                        <Box
                          bg={{ base: 'white', _dark: 'gray.800' }}
                          borderWidth="1px"
                          borderColor={{ base: '#0000001a', _dark: 'gray.600' }}
                          borderRadius="lg"
                          p={4}
                          shadow="md"
                        >
                          <Flex align="start" gap={4}>
                            {/* Step Number Circle */}
                            <Box flexShrink={0}>
                              <Center
                                w="64px"
                                h="64px"
                                bg={"var(--brand-tint-strong)"}
                                borderRadius="full"
                              >
                                <VStack spacing={0}>
                                  <Text fontSize="xs" fontWeight="medium" color="#155dfc">
                                    Step
                                  </Text>
                                  <Text fontSize="lg" fontWeight="bold" color="blue.800">
                                    {step.step}
                                  </Text>
                                </VStack>
                              </Center>
                            </Box>

                            {/* Step Content */}
                            <Box flex={1}>
                              <Flex justify="space-between" align="center" mb={3}>
                                <Box>
                                  <Heading size="md" color="gray.800" mb={1}>
                                    {step.channel_group}
                                  </Heading>
                                  <Text fontSize="sm" color="gray.600">
                                    {index === 0
                                      ? "Primary channel - all messages start here"
                                      : "Fallback channel - receives failed messages from previous step"}
                                  </Text>
                                </Box>
                                <Box textAlign="right">
                                  <Text fontSize="lg" fontWeight="bold" color="#155dfc">
                                    {successRate}%
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    Success Rate
                                  </Text>
                                </Box>
                              </Flex>

                              {/* Progress Bar */}
                              <Box mb={3}>
                                <Flex justify="space-between" fontSize="sm" mb={1}>
                                  <Text color="gray.600">
                                    {step.received.toLocaleString()} messages received
                                  </Text>
                                  <Text color="gray.600">
                                    {step.delivered.toLocaleString()} delivered • {step.failed.toLocaleString()} failed
                                  </Text>
                                </Flex>
                                <Box w="full" bg="gray.200" borderRadius="full" h="12px">
                                  <Flex h="12px" borderRadius="full" overflow="hidden">
                                    <Box
                                      bg="#00c951"
                                      w={`${step.delivered_rate}%`}
                                      transition="all 0.5s"
                                    />
                                    <Box
                                      bg="#fb2c36"
                                      w={`${step?.failure_rate}%`}
                                      transition="all 0.5s"
                                    />
                                  </Flex>
                                </Box>
                              </Box>

                              {/* Results */}
                              <Grid templateColumns="repeat(3, 1fr)" gap={4} fontSize="sm">
                                <Box
                                  bg={"#f0fdf4"}
                                  p={3}
                                  borderRadius="lg"
                                  textAlign="center"
                                >
                                  <Text color="green.600" fontWeight="medium">
                                    ✓ Delivered
                                  </Text>
                                  <Text fontSize="xl" fontWeight="bold" color="green.700">
                                    {step.delivered.toLocaleString()}
                                  </Text>
                                  <Text fontSize="xs" color="green.600">
                                    Messages completed
                                  </Text>
                                </Box>
                                <Box
                                  bg={"#fef2f2"}
                                  p={3}
                                  borderRadius="lg"
                                  textAlign="center"
                                >
                                  <Text color="red.600" fontWeight="medium">
                                    ✗ Failed
                                  </Text>
                                  <Text fontSize="xl" fontWeight="bold" color="red.700">
                                    {step.failed.toLocaleString()}
                                  </Text>
                                  <Text fontSize="xs" color="red.600">
                                    {isLastStep ? "Final failures" : "Sent to next channel"}
                                  </Text>
                                </Box>
                                <Box
                                  bg={"var(--brand-tint)"}
                                  p={3}
                                  borderRadius="lg"
                                  textAlign="center"
                                >
                                  <Text color="#155dfc" fontWeight="medium">
                                    Outcome
                                  </Text>
                                  <Text fontSize="xl" fontWeight="bold" color="blue.700">
                                    {isLastStep ? "END" : step.toNext.toLocaleString()}
                                  </Text>
                                  <Text fontSize="xs" color="#155dfc">
                                    {isLastStep ? "Journey complete" : "To next step"}
                                  </Text>
                                </Box>
                              </Grid>

                              {/* Next Step Preview */}
                              {!isLastStep && step.toNext > 0 && (
                                <Box mt={3} p={2} bg="#fbf9faae" borderRadius="md" textAlign="center">
                                  <Text fontSize="sm" color="gray.600">
                                    → {step.toNext.toLocaleString()} messages will be attempted via {stats.fallbackSteps[index + 1].channel_group}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          </Flex>
                        </Box>
                      </Box>
                    );
                  })}
                </VStack>

                {/* Summary */}
                <Box
                  mt={6}
                  bgGradient="linear(to-r, #ecfdf5, #ecfdf5)"
                  p={4}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="green.200"
                >
                  <Heading size="sm" color="green.800" mb={2}>
                    Fallback System Summary
                  </Heading>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} fontSize="sm">
                    <Box>
                      <Text color="green.700" fontWeight="medium">
                        ✓ System Effectiveness
                      </Text>
                      <Text color="green.600">
                        Started with {stats.totalMessages.toLocaleString()} messages,
                        delivered {stats.successfullyDelivered.toLocaleString()} successfully
                        ({stats.successRate.toFixed(1)}% overall success rate)
                      </Text>
                    </Box>
                    <Box>
                      <Text color="green.700" fontWeight="medium">
                        ⚡ Fallback Performance
                      </Text>
                      <Text color="green.600">
                        {stats.finalFailures.toLocaleString()} messages failed after all attempts
                        ({((stats.finalFailures / stats.totalMessages) * 100).toFixed(1)}% failure rate)
                      </Text>
                    </Box>
                  </Grid>
                </Box>
              </Box>
            </>
          ) : null}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default FallbackPerformanceAnalysis;